/**
 * Plan Repository - Training Plan Data Access Layer
 *
 * This module provides a repository pattern for accessing training plans.
 * Plans are loaded from JSON files and cached in memory to avoid repeated
 * network requests. The repository provides methods to:
 * - List all available training plans
 * - Find a specific plan by ID
 * - Fetch and cache plan details on demand
 */
import fetch from 'cross-fetch';
import { Config } from './config';
import { plans } from './planList';
import { PlanSummary, TrainingPlan, Sport } from 'types/app';

/**
 * Constructs the URL for a training plan JSON file
 */
function url(summary: PlanSummary, sport?: Sport): string {
  const planPath = sport ? Config.getSportPlansPath(sport) : Config.plansPath;
  return planPath + summary[0] + '.json';
}

/**
 * Repository for training plans.
 * Fetches plans on demand and caches them in memory to improve performance.
 */
class PlanRepo {
  // In-memory cache of fetched training plans
  private readonly _cache = new Map<string, TrainingPlan>();

  // List of all available plan summaries
  private readonly _available: PlanSummary[];

  // Lookup map for finding plans by ID
  private readonly _byId: { [id: string]: PlanSummary };

  constructor(available: PlanSummary[]) {
    this._available = available;

    // Build lookup map from plan ID to plan summary
    const initialMap: { [id: string]: PlanSummary } = {};
    this._byId = plans.reduce(function (m, p) {
      m[p[0]] = p;
      return m;
    }, initialMap);
  }

  /**
   * Returns all available training plans
   */
  get available(): PlanSummary[] {
    return this._available;
  }

  /**
   * Finds a plan by ID, returns default plan if not found
   */
  find(planId: string): PlanSummary {
    return this._byId[planId] ? this._byId[planId] : this._byId['pfitz_18_55'];
  }

  /**
   * Returns the first available plan
   */
  get first(): PlanSummary {
    return this._available[0];
  }

  /**
   * Checks if a plan is already cached (for testing)
   */
  isCached(a: PlanSummary, sport?: Sport): boolean {
    return this._cache.has(url(a, sport));
  }

  /**
   * Fetches a training plan, using cache if available
   */
  async fetch(a: PlanSummary, sport?: Sport): Promise<TrainingPlan> {
    return await fetchWithCache(url(a, sport), this._cache);
  }
}

/**
 * Generic fetch function that retrieves JSON from a URL
 */
async function fetchFromUrl<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const error = await res.json();
    return Promise.reject(error);
  }
  const result = await res.json();
  return result;
}

/**
 * Fetches data with caching to avoid duplicate requests
 */
async function fetchWithCache<T>(
  url: string,
  cache: Map<string, T>,
): Promise<T> {
  // Return from cache if available
  if (cache.has(url)) {
    const result = cache.get(url);
    if (!result) {
      throw Error('Assertion error: cached object not found');
    }
    return result;
  }

  // Fetch from network and add to cache
  const res: T = await fetchFromUrl(url);
  cache.set(url, res);
  return res;
}

// Export singleton instance
export const repo = new PlanRepo(plans);
